import { useState } from 'react';
import { Button } from '../Button';
import { RegisterLayout } from './RegisterLayout';
import { useEventState } from '../../lib/eventState';

/**
 * Step 1 — chair landing screen for anonymous users (Figma /reg-1).
 *
 * Composition matches the Figma snapshot: the JNS hex lockup sits at center-
 * left with the Secretlab chair to its right, prize copy underneath, and the
 * "Enter Your Email" CTA below that. (Label was "Log In" — renamed per GHS5-54
 * since the flow is OTP-only and "Log In" implied an existing password account.)
 *
 * Shown only when the visitor has no auth session. Logged-in users skip this
 * step entirely and resume at the first incomplete one (profile if PENDING,
 * straight to /play if ACTIVE).
 */
type LandingStepProps = {
  onContinue: () => void;
};

export function LandingStep({ onContinue }: LandingStepProps) {
  // Contest-end / Free Play (GHS5-85): drop the chair + prize copy and center
  // the JNS lockup — Free Play has no prize.
  const { contestEnded } = useEventState();
  // The CTA click is synchronous — no API call — but the AnimatePresence
  // exit fade gives the user ~0.25s to double-tap the button before this
  // step unmounts. `transitioning` blocks re-entry during that window.
  const [transitioning, setTransitioning] = useState(false);
  const handleClick = (): void => {
    if (transitioning) return;
    setTransitioning(true);
    onContinue();
  };
  return (
    <RegisterLayout>
      <div className="flex flex-col items-center gap-[24px] text-center">
        {/*
          JNS lockup + chair — side-by-side composition per Figma. The lockup
          group caps at 830px on desktop (per direct spec) and scales fluidly
          below that so the composition reads at every breakpoint. Internal
          widths are percentages of the group so the JNS:chair ratio stays
          locked as the group grows/shrinks.
          The chair sits BEHIND the JNS hex (z-0 vs z-10) and is pulled
          leftward via a negative margin so its left edge tucks behind the
          right side of the hex — matches the Figma reg-1 composition.
          No red dropshadow on the JNS lockup here (unlike the Hero where it
          carries one) — the designer intentionally drops the glow on this
          surface so the dark Infoback card reads cleanly.
        */}
        <div className="relative flex w-full max-w-[830px] items-end justify-center">
          {/*
            Percentages sum to ~100% of the 830px container so the composition
            fills it edge-to-edge. The previous max-w caps on the children
            (560 / 200) capped them well below their fair share of 830 — at
            830 wide they bottomed out around 760, leaving visible gutters.
            Letting the percentages drive lets the composition scale with the
            container at every viewport.
          */}
          <picture className={contestEnded ? 'relative z-10 block w-[62%]' : 'relative z-10 block w-[72%]'}>
            {/* <source
              media="(max-width: 640px)"
              srcSet="/assets/images/jns/jns-mobile.png"
            />
            <source
              media="(max-width: 1024px)"
              srcSet="/assets/images/jns/jns-tablet.png"
            /> */}
            <img
              src="/assets/images/jns/jns.svg"
              alt="Jake's Neighborhood Swarm"
              width={880}
              height={607}
              className="pointer-events-none w-full select-none"
            />
          </picture>
          {!contestEnded && (
            <picture className="relative z-0 -ml-[2%] block w-[34%] shrink-0">
              <source
                media="(max-width: 640px)"
                srcSet="/assets/images/secretlab/chair-double.webp"
              />
              <img
                src="/assets/images/secretlab/chair-double.webp"
                alt=""
                aria-hidden="true"
                width={480}
                height={484}
                className="pointer-events-none w-full select-none drop-shadow-[0_18px_24px_rgba(0,0,0,0.45)]"
              />
            </picture>
          )}
        </div>

        {!contestEnded && (
          <p className="max-w-[560px] font-['Arial',sans-serif] text-[clamp(15px,4vw,20px)] leading-[1.4] tracking-[0.04em] text-white">
            Be in the top 5 each week to win a{' '}<br />
            <span className="font-['Arial-Bold','Arial',sans-serif] font-bold">
              State Farm themed Secretlab TITAN Evo chair!
            </span>
          </p>
        )}
        <Button
          variant="dark"
          onClick={handleClick}
          disabled={transitioning}
          className="min-w-[200px]"
        >
          Enter Your Email
        </Button>
      </div>
    </RegisterLayout>
  );
}
